import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { isObject } from "lodash-es";
import { twJoin } from "tailwind-merge";
import { useSessionStorage } from "usehooks-ts";

import {
  DataList,
  DataListRow,
  DataValue as DataValueBase,
} from "src/base-components/DataList";
import { MultilineDataValue } from "src/base-components/MultilineDataValue";
import { PillToggle } from "src/base-components/PillToggle";
import {
  INTERNAL_DATA_KEYS,
  TAKTILE_TYPE,
} from "src/constants/InternalDataKeys";
import { DecisionDataTree } from "src/dataTable/DetailedView/tabs/DecisionDataTree";
import {
  ExpectedDataPopover,
  MatchPill,
  MismatchPill,
} from "src/dataTable/ExpectedDataCell";
import { hasExpectedData } from "src/dataTable/TableUtils";
import { ResultDataAndAuxRowV2 } from "src/dataTable/types";
import { isTaktileObject } from "src/dataTable/typingDecoding";
import { EmptyState } from "src/design-system/EmptyState";
import { loadingTreeData } from "src/entities/entityView/Tree";
import { useSelectedResultsRowIndex } from "src/flowContainer/AuthoringUIContext";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { deepOmit } from "src/utils/objectUtils";
import { isProbablyMarkdown } from "src/utils/stringUtils";

type InspectDataProps = {
  row?: ResultDataAndAuxRowV2 | undefined;
  rowData: Record<string, unknown> | undefined;
  accessedFields: string[];
  isFetching: boolean;
  isHistorical?: boolean;
  withAccessedFields: boolean;
  selectedRowIndex: number | null;
  dividers?: boolean;
  borders?: boolean;
  decisionId?: string;
};

export const InspectData: React.FC<
  Omit<InspectDataProps, "selectedRowIndex">
> = (props) => {
  const selectedRowIndex = useSelectedResultsRowIndex();

  return <InspectDataBase {...props} selectedRowIndex={selectedRowIndex} />;
};
enum InspectDataListView {
  ALL_FIELDS = "all_fields",
  ACCESSIBLE_FIELDS = "accessible_fields",
}

export const InspectDataBase: React.FC<InspectDataProps> = ({
  row,
  rowData,
  accessedFields,
  isFetching,
  isHistorical = false,
  withAccessedFields,
  selectedRowIndex,
  dividers = false,
  borders = false,
  decisionId,
}) => {
  const featureGates = useWorkspaceFeatureGates();
  const isOutputRow = row && hasExpectedData(row);
  const isLoading = isFetching || !rowData;
  const [selectedField, setSelectedField] =
    useSessionStorage<InspectDataListView>(
      "inspect-data-list-view",
      InspectDataListView.ALL_FIELDS,
    );

  const displayDataRows = Object.entries(rowData ?? {})
    .filter(([key]) => !INTERNAL_DATA_KEYS.includes(key))
    .map(
      ([key, value]) =>
        [
          key as string,
          featureGates.entitiesEnabled && isTaktileObject(value) ? (
            value
          ) : (
            <DataValue
              key={key}
              decisionId={decisionId}
              expectedOutputData={
                isOutputRow ? row?.expectedOutputData : undefined
              }
              expectedOutputKeysMismatch={
                isOutputRow ? row?.expectedOutputKeysMismatch : undefined
              }
              field={key}
              value={deepOmit(value, TAKTILE_TYPE)}
            />
          ),
        ] as [string, React.ReactNode | object],
    );

  const filteredRows = withAccessedFields
    ? displayDataRows.filter(
        ([id]) =>
          selectedField === InspectDataListView.ALL_FIELDS ||
          (selectedField === InspectDataListView.ACCESSIBLE_FIELDS &&
            accessedFields?.includes(id)),
      )
    : displayDataRows;

  return (
    <div
      className="flex h-full min-h-0 flex-1 flex-col gap-y-2"
      data-loc="inspect-data-fields"
    >
      {withAccessedFields && (
        <PillToggle value={selectedField} onChange={setSelectedField}>
          <PillToggle.Button value={InspectDataListView.ALL_FIELDS}>
            All fields
          </PillToggle.Button>
          <PillToggle.Button value={InspectDataListView.ACCESSIBLE_FIELDS}>
            Accessed fields
          </PillToggle.Button>
        </PillToggle>
      )}

      <div
        className={twJoin(
          "decideScrollbar h-full min-h-0 flex-1 pr-4",
          dividers && "divide-y divide-gray-100",
        )}
      >
        {isLoading || filteredRows.length > 0 ? (
          <NodeData
            borders={borders}
            dividers={dividers}
            isLoading={isLoading}
            rows={filteredRows}
          />
        ) : (
          <EmptyState
            description={getEmptyStateDescription(
              selectedField,
              isHistorical,
              selectedRowIndex,
            )}
            headline={
              selectedField === InspectDataListView.ACCESSIBLE_FIELDS
                ? "No fields accessed"
                : "No fields found"
            }
            icon={faSearch}
          />
        )}
      </div>
    </div>
  );
};

const DataValue: React.FC<{
  field: string;
  value: unknown;
  expectedOutputData?: Record<string, unknown>;
  expectedOutputKeysMismatch?: string[];
  decisionId?: string;
}> = ({
  field,
  value,
  expectedOutputData,
  expectedOutputKeysMismatch,
  decisionId,
}) => {
  const isRowWithExpectedData = expectedOutputData?.[field] !== undefined;
  const isMatch =
    isRowWithExpectedData && !expectedOutputKeysMismatch?.includes(field);

  if (Array.isArray(value) || isObject(value)) {
    return (
      <div className="flex w-full flex-col items-start gap-y-1.5">
        <DataValueBase decisionId={decisionId} field={field} value={value} />

        {isRowWithExpectedData && (
          <ExpectedDataPopover
            actualOutput={{
              value: JSON.stringify(value),
              isMatch,
            }}
            expectedOutput={{
              value: JSON.stringify(expectedOutputData?.[field]),
              isMatch,
            }}
          >
            {isMatch ? <MatchPill /> : <MismatchPill />}
          </ExpectedDataPopover>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-between">
      {typeof value === "string" && isProbablyMarkdown(value) ? (
        <MultilineDataValue fieldName={field} value={value} />
      ) : (
        <DataValueBase
          decisionId={decisionId}
          field={field}
          value={typeof value === "string" ? JSON.stringify(value) : value}
        />
      )}
      {isRowWithExpectedData && (
        <ExpectedDataPopover
          actualOutput={{
            value: String(value),
            isMatch,
          }}
          expectedOutput={{
            value: String(expectedOutputData?.[field]),
            isMatch,
          }}
        >
          {isMatch ? <MatchPill /> : <MismatchPill />}
        </ExpectedDataPopover>
      )}
    </div>
  );
};

const getEmptyStateDescription = (
  selectedField: InspectDataListView,
  isHistorical: boolean,
  selectedRowIndex: number | null,
) => {
  if (selectedField === InspectDataListView.ACCESSIBLE_FIELDS) {
    return isHistorical
      ? `This node did not access any fields for this decision`
      : `This node did not access any fields for #${
          (selectedRowIndex || 0) + 1
        } test case during the last test run`;
  }

  return isHistorical
    ? "This decision had no fields for this decision"
    : "This test case had no fields during the last test run";
};

const NodeData: React.FC<{
  rows: [string, React.ReactNode | object][];
  isLoading: boolean;
  borders: boolean;
  dividers: boolean;
}> = ({ rows, isLoading, borders, dividers }) => {
  const featureGates = useWorkspaceFeatureGates();

  if (featureGates.featuresEventsEnabled || featureGates.entitiesEnabled) {
    if (isLoading) {
      return <DecisionDataTree data={loadingTreeData} loading />;
    }

    return (
      <DecisionDataTree data={Object.fromEntries(rows)} loading={isLoading} />
    );
  }

  if (isLoading) {
    return <DataList borders={borders} dividers={dividers} isLoading />;
  }

  return (
    <DataList
      borders={borders}
      dividers={dividers}
      rows={rows as DataListRow[]}
    />
  );
};
